import { Box, CircularProgress, Typography } from "@material-ui/core";
import { useEffect, useState } from "react";
import service, { S3DataFile } from "./DataService";
import websocketService from "../websocket/WebSocketService";

const INITIAL_POLL_WINDOW_MS = 30_000;
const INITIAL_POLL_INTERVAL_MS = 2_000;
const FOLLOW_UP_POLL_INTERVAL_MS = 5_000;
const MAX_POLL_DURATION_MS = 5 * 60_000;
const WEBSOCKET_WAIT_MS = 10_000;

export function DataExportPanel() {
  const [dataFile, setDataFile] = useState(undefined as S3DataFile | undefined);
  const [error, setError] = useState(undefined as string | undefined);
  const [jobId, setJobId] = useState(undefined as string | undefined);

  useEffect(() => {
    let mounted = true;
    let activeJobId = "";
    let polling = false;
    let pollStart = 0;
    let pollTimer: ReturnType<typeof setTimeout> | undefined;
    let websocketFallbackTimer: ReturnType<typeof setTimeout> | undefined;

    const clearPolling = () => {
      if (pollTimer) {
        clearTimeout(pollTimer);
        pollTimer = undefined;
      }
      polling = false;
    };

    const stopWebsocketFallback = () => {
      if (websocketFallbackTimer) {
        clearTimeout(websocketFallbackTimer);
        websocketFallbackTimer = undefined;
      }
    };

    const updateFromStatus = async () => {
      if (!activeJobId || !mounted) {
        return;
      }
      try {
        const status = await service.getExportStatus(activeJobId);
        if (!mounted || status.jobId !== activeJobId) {
          return;
        }
        if (status.status === "COMPLETED") {
          setDataFile({
            key: status.key,
            signedUrl: status.signedUrl,
          });
          setError(undefined);
          clearPolling();
          stopWebsocketFallback();
          return;
        }
        if (status.status === "FAILED") {
          setError(status.error);
          setDataFile(undefined);
          clearPolling();
          stopWebsocketFallback();
        }
      } catch (err) {
        if (!mounted) {
          return;
        }
        setError(
          `Failed to check export status: ${
            err instanceof Error ? err.message : String(err)
          }`
        );
        clearPolling();
        stopWebsocketFallback();
      }
    };

    const schedulePolling = () => {
      if (!mounted || !activeJobId || !polling) {
        return;
      }
      const elapsed = Date.now() - pollStart;
      if (elapsed > MAX_POLL_DURATION_MS) {
        setError("Export timed out while waiting for completion");
        clearPolling();
        return;
      }
      const interval =
        elapsed < INITIAL_POLL_WINDOW_MS
          ? INITIAL_POLL_INTERVAL_MS
          : FOLLOW_UP_POLL_INTERVAL_MS;
      pollTimer = setTimeout(() => {
        updateFromStatus().finally(() => {
          if (polling) {
            schedulePolling();
          }
        });
      }, interval);
    };

    const startPolling = () => {
      if (polling || !activeJobId) {
        return;
      }
      polling = true;
      pollStart = Date.now();
      schedulePolling();
    };

    service
      .initiateExport()
      .then((response) => {
        if (!mounted) {
          return;
        }
        activeJobId = response.jobId;
        setJobId(response.jobId);
        setError(undefined);

        if (!websocketService.isAlive()) {
          startPolling();
        } else {
          websocketFallbackTimer = setTimeout(() => {
            startPolling();
          }, WEBSOCKET_WAIT_MS);
        }
      })
      .catch((err) => {
        if (!mounted) {
          return;
        }
        setError(`Failed to initiate export: ${err.message}`);
      });

    const subscription = websocketService.dataExportSubject.subscribe(
      (message) => {
        if (!activeJobId || message.jobId !== activeJobId) {
          return;
        }
        stopWebsocketFallback();

        if ("error" in message) {
          setError(message.error);
          setDataFile(undefined);
          clearPolling();
          return;
        }

        updateFromStatus();
      }
    );

    return () => {
      mounted = false;
      clearPolling();
      stopWebsocketFallback();
      subscription.unsubscribe();
    };
  }, []);

  return (
    <Box>
      {dataFile ? (
        <Box>
          <a href={dataFile.signedUrl} target="_blank" rel="noreferrer" download>
            {dataFile.key}
          </a>
          <br />
        </Box>
      ) : error ? (
        <Box>
          <Typography color="error">Export failed: {error}</Typography>
        </Box>
      ) : (
        <Box>
          <Box>
            <CircularProgress size={16} /> Exporting data ...
          </Box>
          {jobId && (
            <Box>
              <Typography variant="caption" style={{ color: "gray" }}>
                Job ID: {jobId}
              </Typography>
            </Box>
          )}
          <Box>
            <Typography variant="caption" style={{ color: "gray" }}>
              Wait a few seconds until the file is generated
            </Typography>{" "}
          </Box>
        </Box>
      )}
    </Box>
  );
}
